/**
 * Test-only experiment override store.
 *
 * Why this exists:
 * The experiment service is called server-side inside getServerSideProps, so
 * Playwright's page.route() cannot intercept those requests — they are Node.js
 * HTTP calls made by the Next.js server, not browser requests.
 *
 * Why it lives here (src/services/):
 * This module runs inside the Next.js server process. It must be co-located with
 * experiment-service.ts so that service can import and check it at SSR time.
 * The Playwright test runner calls POST /api/app-form/experiments (see src/pages/api/app-form/experiments.ts)
 * before navigating, which populates this store. ExperimentService then reads the
 * store and short-circuits the real API call, returning the override value instead.
 *
 * This endpoint and store are disabled in production and qa (TEST_ENV !== 'playwright').
 */

// Store overrides on the Node.js global object so they survive Next.js HMR module re-evaluation in dev mode.
// A module-level Map would be reset on every hot reload, wiping overrides set between the POST and page.goto().
declare global {
  // eslint-disable-next-line no-var
  var __experimentOverrides: Map<string, boolean> | undefined
}

const overrides: Map<string, boolean> = global.__experimentOverrides ?? (global.__experimentOverrides = new Map())

export const setExperimentOverrides = (experiments: Record<string, boolean>): void => {
  for (const [name, enabled] of Object.entries(experiments)) {
    overrides.set(name, !!enabled)
  }
}

export const getExperimentOverride = (name: string): boolean | undefined =>
  overrides.get(name)
